import useAsyncControl from '@onepercentio/one-ui/hooks/useAsyncControl'
import { Sale } from 'core/logic/asset/asset.types'
import { useTenant } from 'core/logic/tenant/tenant.hook'
import { useStableBalance } from 'core/logic/token/token.hook'
import { useUser } from 'core/logic/user'

import { OfferForm } from 'pages/Authenticated/Marketplace/CreateOffer/CreateOffer.types'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { useMarketplace, useMarketplaceInterface } from './Marketplace'
import { doUpdateWallet } from 'core/modules/firebase/service'

import { useSignMessage } from 'core/logic/wallet'
import { ERC1155ABI } from 'core/constants/ERC1155ABI'
import { OfferType } from 'core/logic/drop/drop.types'
import useRouteGalleryOrDefault from 'openspace/hooks/useRoutePathGallery'
import useWeb3Provider from 'hooks/useWeb3Provider'
import { ContractType } from 'core/logic/contract/types/types'
import { ERC721ABI } from 'core/constants/ERC721'

export type MarketplaceUserContextShape = {
  balance: {
    [currency: string]: number | undefined
  }
  updateBalance: () => void

  updateWallet: (wallet: string) => Promise<void>
  buyOffer: (offer: Sale) => Promise<void | string>

  createOffer: (offer: OfferForm) => Promise<Sale['offerId']>
  removeOffer: (offerId: string) => Promise<void>
  updateOffer: (offerId: string, offer: OfferForm) => Promise<void>
  getNftMovementAllowance: (tokenId?: number) => Promise<boolean>
  approveNFTManagement: (tokenId?: number) => Promise<void>
  createOfferOnly: (offer: OfferForm) => Promise<OfferType['id']>

  getTokensAllowance: (token: string, price: number) => Promise<number>
  approveTokenAllowance: (token: string, basePrice: number) => Promise<void>
}

export const Context = createContext<MarketplaceUserContextShape>(null as any)

export function MarketplaceUserProvider({ children }: PropsWithChildren<{}>) {
  const gallery = useRouteGalleryOrDefault()
  const { profile } = useUser()
  const { tenant } = useTenant()
  const { sales } = useMarketplace()

  const web3MetamaskProvider = useWeb3Provider()

  const { stableBalance: currencyBalanceMap, updateBalance } = useStableBalance(
    profile?.wallet
  )

  const marketplaceInterface = useMarketplaceInterface()

  const signMessageHook = useSignMessage()
  const nftContractDefinition = gallery

  const erc1155Contract = useMemo(() => {
    if (!web3MetamaskProvider) return
    const abi = (() => {
      switch (nftContractDefinition.smartContract?.type) {
        case ContractType.Collectibles:
        case ContractType.RarumNFT:
          return ERC1155ABI
        case ContractType.DigitalTwin:
        case ContractType.Loyalty:
          return ERC721ABI
      }
    })()
    return abi
      ? (new web3MetamaskProvider.eth.Contract(
          abi,
          nftContractDefinition.smartContract!.address
        ) as unknown as Contract.ERC1155)
      : null
  }, [nftContractDefinition, web3MetamaskProvider])

  const _getNFTMovementAllowance = useCallback(
    (tokenId?: number) => {
      return marketplaceInterface!.canMarketplaceManageTokens(
        erc1155Contract!,
        tokenId
      )
    },
    [erc1155Contract, profile, tenant, marketplaceInterface]
  )

  async function _getTokensAllowance(token: string, price: number) {
    const permissionedAllowance =
      await marketplaceInterface!.allowanceForCurrency(token, price)
    return permissionedAllowance
  }

  async function _createOffer(offer: OfferForm) {
    const offerId = await marketplaceInterface!.createOffer(
      nftContractDefinition.smartContract?.address!,
      offer
    )
    sales.getAll()
    return offerId
  }

  return (
    <Context.Provider
      value={{
        approveTokenAllowance: (...args) =>
          marketplaceInterface!.setCurrencyAllowance!(...args),
        getTokensAllowance: _getTokensAllowance,
        updateBalance: updateBalance,
        createOffer: async (offer) => {
          const isApproved = _getNFTMovementAllowance(offer.tokenId)
          if (!isApproved) {
            await marketplaceInterface!.approveMarketplaceToManageTokens(
              gallery.id,
              nftContractDefinition.smartContract!.address
            )
          }

          return await _createOffer(offer)
        },
        removeOffer: (...args) => marketplaceInterface!.removeOffer(...args),
        updateOffer: marketplaceInterface?.updateOffer!,
        buyOffer: (sale) => marketplaceInterface!.buyOffer(sale),
        updateWallet: async (wallet) => {
          const message = {
            account: wallet,
            timestamp: Date.now().toString(),
          }
          const signature = await signMessageHook.signMessageFn({
            address: wallet,
            message: JSON.stringify(message, null, 2),
          })

          await doUpdateWallet(
            {
              ...message,
              message: 'Connect my account: ' + wallet,
            },
            signature
          )
        },
        balance: currencyBalanceMap,
        getNftMovementAllowance: _getNFTMovementAllowance,
        approveNFTManagement: (tokenId?: number) =>
          marketplaceInterface!.approveMarketplaceToManageTokens(
            gallery.id,
            nftContractDefinition.smartContract!.address,
            tokenId
          ),
        createOfferOnly: _createOffer,
      }}>
      {children}
    </Context.Provider>
  )
}

export function useMarketplaceUser(): MarketplaceUserContextShape & {
  control: ReturnType<typeof useAsyncControl>
} {
  const {
    getNftMovementAllowance,
    approveNFTManagement,
    createOfferOnly,
    getTokensAllowance,
    approveTokenAllowance,
    buyOffer,
    ...context
  } = useContext(Context)
  const asyncControl = useAsyncControl({
    getNftMovementAllowance,
    approveNFTManagement,
    createOfferOnly,
    getTokensAllowance,
    approveTokenAllowance,
    buyOffer,
  })
  const updateWallet = useCallback<MarketplaceUserContextShape['updateWallet']>(
    (...args) => {
      return asyncControl.process(async () => {
        await context.updateWallet(...args)
      })
    },
    []
  )
  const createOffer = useCallback<MarketplaceUserContextShape['createOffer']>(
    (...args) => {
      return new Promise((r, rej) => {
        return asyncControl.process(async () => {
          return await context
            .createOffer(...args)
            .then(r)
            .catch((e) => {
              rej(e)
              return Promise.reject(e)
            })
        })
      })
    },
    []
  )

  return {
    ...context,
    control: asyncControl,

    updateWallet,
    createOffer,
    getNftMovementAllowance: asyncControl.getNftMovementAllowance,
    approveNFTManagement: asyncControl.approveNFTManagement,
    createOfferOnly: asyncControl.createOfferOnly,
    getTokensAllowance: asyncControl.getTokensAllowance,
    approveTokenAllowance: asyncControl.approveTokenAllowance,
    buyOffer: asyncControl.buyOffer,
  }
}
